import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PollWidget } from "@/components/PollWidget";
import { TrendBadge } from "@/components/charts";
import { api } from "@/lib/api";
import { Megaphone, BarChart3, ExternalLink, Users } from "lucide-react";

/** Municipality "Community Pulse" (Phase 8): real aggregated data only —
 * top issues + trending + ward pulses (each level independently threshold-protected). */
export const CommunityPulse = ({ municipalityId, municipalityName }) => {
  const [today, setToday] = useState(null);
  const [pulse, setPulse] = useState(null);

  useEffect(() => {
    if (!municipalityId) return;
    api.get("/public/polls/today", { params: { municipality_id: municipalityId } })
      .then((r) => setToday(r.data)).catch(() => {});
    api.get("/public/intelligence/pulse", { params: { municipality_id: municipalityId } })
      .then((r) => setPulse(r.data)).catch(() => {});
  }, [municipalityId]);

  if (!municipalityId || (!today?.poll && !pulse)) return null;

  return (
    <Card data-testid="community-pulse-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone className="h-4 w-4 text-accent" />
          <h2 className="font-heading font-bold text-lg text-foreground">Community Pulse</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Voluntary community polls — not a scientific opinion poll. Never connected to voting declarations.
        </p>

        {pulse?.available && (
          <p className="text-sm font-semibold text-foreground mb-3 inline-flex items-center gap-1.5" data-testid="pulse-response-total">
            <Users className="h-4 w-4 text-accent" /> {pulse.response_count} Community Poll Responses
          </p>
        )}

        {today?.poll && (
          <div className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Today's Question</p>
            <PollWidget poll={today.poll} municipalityId={municipalityId} compact />
          </div>
        )}

        {pulse?.available && pulse.top_issues?.length > 0 && (
          <div data-testid="pulse-top-issues">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 inline-flex items-center gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" /> Top Issues
              <span className="normal-case font-normal">· Responses: {pulse.response_count}</span>
            </p>
            <div className="space-y-2">
              {pulse.top_issues.map((i) => (
                <div key={i.label}>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground">{i.label}</span>
                    <span className="tabular-nums text-muted-foreground">{i.percent}%</span>
                  </div>
                  <Progress value={i.percent} className="h-1.5 mt-1" />
                </div>
              ))}
            </div>
          </div>
        )}

        {pulse?.available && pulse.trending?.length > 0 && (
          <div className="mt-4" data-testid="pulse-trending">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Trending</p>
            <ul className="space-y-1.5">
              {pulse.trending.map((t) => (
                <li key={t.label} className="flex items-center justify-between gap-2 text-sm flex-wrap">
                  <span className="text-foreground">{t.label}</span>
                  <TrendBadge trend={t} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {pulse?.available && pulse.ward_pulses?.length > 0 && (
          <div className="mt-4" data-testid="pulse-ward-section">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">What ward residents are saying</p>
            <div className="space-y-3">
              {pulse.ward_pulses.map((w) => (
                <div key={w.ward} className="rounded-lg border border-slate-200 p-3" data-testid={`ward-pulse-${w.ward.replace(/\W+/g, "-").toLowerCase()}`}>
                  <p className="text-xs font-semibold text-foreground">{w.ward} <span className="font-normal text-muted-foreground">· Responses: {w.response_count}</span></p>
                  <ul className="mt-1.5 space-y-0.5">
                    {w.top_issues.map((i) => (
                      <li key={i.label} className="text-xs flex justify-between">
                        <span>{i.label}</span>
                        <span className="tabular-nums text-muted-foreground">{i.percent}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {pulse && !pulse.available && (
          <p className="text-xs text-muted-foreground" data-testid="pulse-threshold-note">
            Local issue rankings appear once enough residents have answered — this protects individual privacy.
          </p>
        )}

        {pulse?.pita_connection && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="pulse-pita-connection">
            <p className="font-heading font-semibold text-sm text-amber-900">{pulse.pita_connection.heading}</p>
            <p className="text-xs text-amber-900/90 mt-1">{pulse.pita_connection.text}</p>
            <div className="flex flex-wrap gap-1.5 mt-2 text-[10px] text-amber-800/90">
              {pulse.pita_connection.flow.map((step, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  {i > 0 && <span>→</span>}<span className="rounded bg-amber-100 px-1.5 py-0.5">{step}</span>
                </span>
              ))}
            </div>
            <Button asChild size="sm" className="mt-2.5 bg-amber-600 hover:bg-amber-700 text-white" data-testid="pulse-pita-button">
              <a href={pulse.pita_connection.url} target="_blank" rel="noopener noreferrer">
                {pulse.pita_connection.button} <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </a>
            </Button>
            <p className="text-[10px] text-amber-800/80 mt-2">{pulse.pita_connection.external_note}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <Button asChild size="sm" className="bg-accent hover:bg-accent/90" data-testid="pulse-full-voice-link">
            <Link to={`/community-voice?municipality=${municipalityId}`}>See Full Community Voice →</Link>
          </Button>
          <Button asChild size="sm" variant="outline" data-testid="pulse-all-polls-link">
            <Link to={`/polls?municipality=${municipalityId}`}>All {municipalityName || "local"} polls</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
